import { ReactNode, useState, CSSProperties } from 'react'
import EchoCard from '@/components/EchoCard'
import Categories, { RadioButton, DropdownCategories } from '@/pages/platform/leaderboard/categories'
import useLocale from '@/utils/useLocale'
import { Filter as IFilter } from '@/types/api'
import styles from './style/filter.module.less'
import { Space, Button, Spin } from '@arco-design/web-react'
import { IconClose } from '@arco-design/web-react/icon'
import cx from 'classnames'
import dayjs from 'dayjs'
import { ProductThreeCategories } from '@/types/api'
import { transTree } from '@/utils/tree'
import { useRouter } from 'next/router'
import { resRealValue } from './components/filter-item'
import { CustomFilterListButton, CustomFilterSaveButton } from './components/custom-filter'
import { NewFilterItem } from './components/filter-item'
import { FilterItemType } from '@/components/FilterItem'
export type OptionsProp = (IFilter & { disabled?: boolean })[]
interface IOptionMap {
  data: IFilter[]
  name: string
}

interface AdvancedFilterItemProp {
  label?: ReactNode
  key: string
  hasCustomRange?: boolean
  suffix?: string
  isCheckbox?: boolean
  isRadioGroup?: boolean
  options?: OptionsProp
  dateRangeOptions?: { value: string; label: string }[]
  resultLabel?: string
  placeholder?: string
  isCheckboxGroup?: boolean
  isDateRangePicker?: boolean
  isDoubleSelect?: boolean
  isMultipleCheckbox?: boolean
  isSelect?: boolean
  isBasicCheckbox?: boolean
  optionMap?: Record<string, IOptionMap>
  defaultField?: string
  tips?: ReactNode | string
  nameSlug?: string
  unit?: string
  type: FilterItemType
}

interface NewFilterItemProp {
  key: string
  label: ReactNode | string
  options: IFilter[]
  optionMap?: Record<string, IOptionMap>
  params?: { [key: string]: any }
  onChange?: (params: { [key: string]: any }) => void
  type: FilterItemType
  tooltipText?: string
}

interface BasicFilterItemProp extends AdvancedFilterItemProp {
  type: FilterItemType
}

export interface MoreFilterItem extends AdvancedFilterItemProp {
  customTitle?: string
  placeholder?: string
  unit?: string
  tips?: ReactNode | string
  defaultField?: string
  tooltipText?: string
}

interface categoryFilterItem {
  label: string
  key: string
  categories: IFilter[]
  threeLevelList?: ProductThreeCategories
  resultLabel?: string
}

type Props<Params> = {
  categoryFilters: categoryFilterItem[]
  basicFilters?: NewFilterItemProp[]
  advancedFilters?: NewFilterItemProp[]
  params: Params
  onChange: (params: { [key: string]: any }) => void
  style?: CSSProperties
  loading?: boolean
  id: string
  showCustomFilter?: boolean
}

interface MoreSelectProp {
  filter: MoreFilterItem
  params: { [key: string]: any }
  onChange: (params: { [key: string]: any }) => void
  style?: CSSProperties
}

export interface RelateCheckboxProp {
  filter: AdvancedFilterItemProp
  params: { [key: string]: any }
  onChange: (params: { [key: string]: any }) => void
  tips?: string
}

export interface CheckboxGroupProps extends MoreSelectProp {
  noMultiple?: boolean //禁止复选
}

export interface ICustomSelect extends MoreSelectProp {
  hasCustomRange?: boolean
  options: IFilter[]
  field: string
}

export interface IDoubleSelect extends MoreSelectProp {
  defaultField?: string
}

interface ResultItemProp {
  text?: string | ReactNode
  onDelete?: () => any
}

function Filter<Params extends { [key: string]: any }>(props: Props<Params>) {
  const {
    categoryFilters,
    params,
    onChange,
    style,
    loading,
    id,
    showCustomFilter,
    advancedFilters = [],
    basicFilters = []
  } = props
  const t = useLocale()
  const router = useRouter()
  const [update, setUpdate] = useState(Date.now())

  const commonDelete = (filter: MoreFilterItem | AdvancedFilterItemProp) => {
    if (router.query[`${filter.key}_name_slug`]) {
      onChange({ [filter.key]: undefined, [`${filter.key}_name_slug`]: 'all' })
    } else {
      const keyList = filter.key.split(',')
      if (keyList.length > 1) {
        keyList.map((key: string) => {
          onChange({ [key]: undefined })
        })
      } else {
        onChange({ [filter.key]: undefined })
      }
    }
  }

  const handleFilterResultItem = (
    filter: MoreFilterItem | AdvancedFilterItemProp | NewFilterItemProp | BasicFilterItemProp
  ) => {
    const keyList = filter.key.split(',')
    if (keyList.length <= 1 && !params[filter.key]) {
      return
    }

    const resObj: ResultItemProp = {
      onDelete: () => {
        commonDelete(filter)
      }
    }
    const labelText = filter.label

    if (filter.type === 'select') {
      const value = params[filter.key]
      const curObj = filter.options?.find((option) => option.id === value)
      if (curObj) {
        resObj.text = `${filter.label}: ${curObj.name}`
      }
    }

    if (filter.type === 'selectInput') {
      const resText = resRealValue(filter.options, params[filter.key], filter?.unit, filter?.suffix)
      resObj.text = `${labelText}: ${resText}`
    }

    if (filter.type === 'dateRangePicker') {
      // 多个key
      const startTimeKey = keyList[0]
      const endTimeKey = keyList[1]
      resObj.text = `${labelText}:${dayjs.unix(params[startTimeKey]).format('YYYY-MM-DD')}-${dayjs
        .unix(params[endTimeKey])
        .format('YYYY-MM-DD')}`
      if (!params[startTimeKey]) {
        return
      }
    }

    if (filter.type === 'multipleCheckbox') {
      const filerKeys = Object.keys(filter.optionMap as any)
      let curKey = ''
      let resultText = ''
      filerKeys?.map((key, index) => {
        if (params[key]) {
          curKey = key
          const customOptions: any = filter.optionMap?.[key] ? filter.optionMap?.[key].data : []
          const currentObj = customOptions.find((item: any) => item.id === params[key])
          resultText += `${currentObj?.name}${index === filerKeys.length - 1 ? '' : '/'}`
        }
      })
      const labelText = filter.label

      resObj.text = `${labelText}: ${resultText}`
      if (!params[curKey]) {
        return
      }
    }

    if (filter.type === 'multipleSelectInput') {
      const filerKeys = Object.keys(filter.optionMap as any)
      let curKey = ''
      let resultText = ''
      let resultLabel = ''
      filerKeys?.map((key, index) => {
        if (params[key]) {
          curKey = key
          const customOptions: any = filter.optionMap?.[key] ? filter.optionMap?.[key].data : []
          resultLabel = filter.optionMap?.[key]?.name || ''
          const currentObj = customOptions.find((item: any) => item.id === params[key])
          resultText += currentObj ? `${currentObj?.name}` : `${params[key]}`
        }
      })

      resObj.text = `${resultLabel}: ${resultText}`
      if (!params[curKey]) {
        return
      }
    }

    return resObj
  }
  const generateFilterResult = () => {
    const categoryFiltersResult = categoryFilters
      .map((filter) => {
        const id = params[filter.key]
        if (!id) {
          return
        }
        let categoryResultText = ''
        if (id && filter?.threeLevelList) {
          const treeList = transTree(filter?.threeLevelList || [])
          if (Array.isArray(id)) {
            if (id.length === 1 && id[0] === '') return
            id.forEach((id: string) => {
              const curObj = treeList.find((item) => item.value === id)
              categoryResultText += `${curObj?.label}/`
            })
            categoryResultText = categoryResultText.slice(0, -1)
          } else if (typeof id === 'string') {
            const curObj = treeList.find((item) => item.value === id)
            categoryResultText = `${curObj?.label}`
          }
        }
        const labelText = filter?.resultLabel || filter?.label
        return {
          text: filter?.threeLevelList
            ? `${labelText}: ${categoryResultText}`
            : `${labelText}: ${filter.categories?.find((item) => item.id === params[filter.key])?.name}`,
          onDelete: () => {
            if (router.query[`${filter.key}_name_slug`]) {
              onChange({
                [filter.key]: filter?.threeLevelList ? filter?.threeLevelList[0].value : filter.categories[0].id,
                [`${filter.key}_name_slug`]: 'all'
              })
            } else {
              onChange({
                [filter.key]: filter?.threeLevelList ? filter?.threeLevelList[0].value : filter.categories[0].id
              })
            }
          }
        }
      })
      .filter(Boolean)

    const basicFiltersResult = basicFilters?.map((filter) => handleFilterResultItem(filter)).filter(Boolean) || []

    const newFiltersResult = advancedFilters?.map((filter) => handleFilterResultItem(filter)).filter(Boolean) || []
    const searchResults = [
      {
        key: 'keyword',
        label: t['search_keywords']
      }
    ]
      .map((filter) => {
        if (!params[filter.key]) {
          return
        }
        const value = params[filter.key]
        return {
          text: `${filter.label}: ${value}`,
          onDelete: () => {
            onChange({ [filter.key]: '' })
          }
        }
      })
      .filter(Boolean)

    return [...categoryFiltersResult, ...searchResults, ...basicFiltersResult, ...newFiltersResult]
  }

  // 基础筛选
  const renderBasicFilters = () => {
    return basicFilters.length > 0 ? (
      <div className="flex">
        <div className={cx(styles.label, styles.labelText)}>{t['common.list.condition_filter']}</div>
        <div className={'flex justify-between flex-1'}>
          <div className={styles['newFilters']}>
            {basicFilters.map((item) => {
              return <NewFilterItem key={item.key} filter={item} params={params} onChange={onChange} type={item.type} />
            })}
          </div>
        </div>
      </div>
    ) : null
  }
  // 新版高级筛选
  const renderNewFilter = () => {
    return advancedFilters.length > 0 ? (
      <div className="flex">
        <div className={cx(styles.label, styles.labelText)}>{t['common.list.advance_filter']}</div>
        <div className={'flex justify-between flex-1'}>
          <div className={styles['newFilters']}>
            {advancedFilters.map((item) => {
              return <NewFilterItem key={item.key} filter={item} params={params} onChange={onChange} type={item.type} />
            })}
          </div>
        </div>
      </div>
    ) : null
  }

  const renderFilterResult = () => {
    const result = generateFilterResult()

    return (
      <div className={styles.filterResult}>
        {result.length ? (
          <>
            <div className={cx(styles.label, styles.labelText)}>{t['common.filter.filtering']}</div>
            <div className={styles.filterResultWrap}>
              {result.map((item, index) => {
                return (
                  item?.onDelete && (
                    <RadioButton key={index} onClick={item?.onDelete}>
                      <Space>
                        {item?.text}
                        <IconClose />
                      </Space>
                    </RadioButton>
                  )
                )
              })}
              <Button
                type="outline"
                shape="round"
                className={styles.clearBtn}
                onClick={() => {
                  result.forEach((item) => item?.onDelete && item?.onDelete())
                }}
              >
                {t['common.filter.clear']}
              </Button>
              {showCustomFilter && (
                <CustomFilterSaveButton
                  params={params}
                  results={generateFilterResult()}
                  saveSuccessCallback={() => setUpdate(Date.now())}
                />
              )}
            </div>
          </>
        ) : null}
      </div>
    )
  }

  return (
    <EchoCard>
      <Spin loading={loading} block>
        <Space direction="vertical" size="small" className={styles.filtersWrap} style={style}>
          {categoryFilters.map((filter) => {
            if (filter?.threeLevelList) {
              return (
                <DropdownCategories
                  id={filter.key}
                  key={filter.key}
                  label={filter.label}
                  value={params[filter.key]}
                  onValuesChange={(values) => {
                    let level1Obj: IFilter | undefined = undefined
                    if (Array.isArray(values)) {
                      level1Obj = filter.categories.find((c) => c.id === values[0])
                    }
                    if (router.query[`${filter.key}_name_slug`]) {
                      onChange({ [filter.key]: values, [`${filter.key}_name_slug`]: level1Obj?.name_slug || 'all' })
                    } else {
                      onChange({ [filter.key]: values })
                    }
                  }}
                  labelClassName={styles.label}
                  threeLevelList={filter.threeLevelList}
                />
              )
            }
            return (
              <Categories
                id={filter.key}
                key={filter.key}
                label={filter.label}
                value={params[filter.key]}
                onChange={(value) => {
                  const nameSlugObj = filter.categories?.find((c) => c.id === value)
                  if (router.query[`${filter.key}_name_slug`]) {
                    onChange({ [filter.key]: value, [`${filter.key}_name_slug`]: nameSlugObj?.name_slug || 'all' })
                  } else {
                    onChange({ [filter.key]: value })
                  }
                }}
                categories={filter.categories}
                labelClassName={styles.label}
              />
            )
          })}

          {renderBasicFilters()}
          {renderNewFilter()}
        </Space>
        <div className={styles.actionsWrap}>
          {renderFilterResult()}
          {showCustomFilter && (
            <CustomFilterListButton params={params} results={generateFilterResult()} onChange={onChange} />
          )}
        </div>
      </Spin>
    </EchoCard>
  )
}

export default Filter
